import { type ReactNode } from 'react'
import classnames from 'classnames'

import { Modal } from '../../../../../components/Modal/Modal'
import { MCP_LOGOS, MCP_LOGOS_MONO, type McpLogo } from './mcpLogos'

import styles from './Transcript.module.scss'

export type TranscriptDownloadFeedback = 'connect_mcp' | 'downloads_work_for_me' | 'interested_but_not_allowed' | 'closed'

const MCP_BENEFITS = [
  'Every meeting, always up to date',
  'Ask questions across all your meetings and more',
  'Your AI gets full context. Not just one transcript.',
]

interface TranscriptDownloadModalProps {
  show: boolean
  mcpAppsEnabled: boolean
  onSelect: (selection: TranscriptDownloadFeedback) => void
}

interface PanelProps {
  title: string
  onClick: () => void
  children: ReactNode
  footer: ReactNode
}

const Panel: React.FC<PanelProps> = ({ title, onClick, children, footer }) => (
  <div
    className={classnames('d-flex flex-column gap-3 p-4 bg-white border zocks-border-color-gray-300 zocks-border-0.5', styles.panel)}
    role='button'
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(event) => (event.key === 'Enter' || event.key === ' ' ? onClick() : undefined)}
  >
    <span className={classnames('zocks-text-gray-900', styles.headingSm)}>{title}</span>
    <div className='d-flex flex-column gap-2 flex-grow-1'>{children}</div>
    {footer}
  </div>
)

interface LogoRowProps {
  logos: McpLogo[]
  suffix?: ReactNode
}

const LogoRow: React.FC<LogoRowProps> = ({ logos, suffix }) => (
  <div className='d-flex align-items-center gap-2'>
    {logos.map((logo) => (
      <span
        key={logo.alt}
        className={classnames('d-flex align-items-center justify-content-center rounded bg-white border zocks-border-color-gray-400', styles.logoBadge)}
      >
        <img className={styles.logoImg} src={logo.src} alt={logo.alt} />
      </span>
    ))}
    {suffix}
  </div>
)

interface DownloadsWorkForMePanelProps {
  onSelect: (selection: TranscriptDownloadFeedback) => void
}

const DownloadsWorkForMePanel: React.FC<DownloadsWorkForMePanelProps> = ({ onSelect }) => (
  <Panel
    title='Downloads work for me'
    onClick={() => onSelect('downloads_work_for_me')}
    footer={<span className='paragraph-medium fw-bold zocks-text-gray-900'>I'll keep downloading</span>}
  >
    <span className='paragraph-medium zocks-text-gray-900'>
      No problem. Download transcripts from any meeting, anytime. You can connect the Zocks MCP whenever you're ready.
    </span>
  </Panel>
)

export const TranscriptDownloadModal: React.FC<TranscriptDownloadModalProps> = ({ show, mcpAppsEnabled, onSelect }) => (
  <Modal
    show={show}
    onHide={() => onSelect('closed')}
    dialogClassName={styles.modalDialog}
    centered
    header={
      <div className='d-flex align-items-center gap-2'>
        <i className='bi bi-check-circle-fill zocks-text-success fs-5' />
        <span className={classnames('zocks-text-gray-900', styles.headingMd)}>Transcript downloaded</span>
      </div>
    }
    body={
      <div className='d-flex flex-column zocks-gap-2.5 pb-5'>
        <span className='paragraph-medium zocks-text-gray-900'>
          This download covers <strong>1 meeting</strong>. Connect an app to access your full Zocks account — all meetings, summaries, and client notes — right
          where you already work.
        </span>
        <div className='d-flex flex-column gap-3 zocks-p-1.25 zocks-bg-color-primary-light zocks-border-0.5'>
          <span className='lead-small zocks-text-gray-900 text-center'>There's a better way to access your data.</span>
          <div className='d-flex flex-wrap gap-3'>
            {mcpAppsEnabled ? (
              <Panel
                title='Connect to Zocks MCP'
                onClick={() => onSelect('connect_mcp')}
                footer={<LogoRow logos={MCP_LOGOS} suffix={<span className='paragraph-medium fw-bold zocks-text-gray-900'>+ Show all</span>} />}
              >
                {MCP_BENEFITS.map((benefit) => (
                  <div key={benefit} className='d-flex align-items-start gap-2'>
                    <i className='bi bi-check2 zocks-text-primary' />
                    <span className='paragraph-medium zocks-text-gray-900'>{benefit}</span>
                  </div>
                ))}
              </Panel>
            ) : (
              <Panel
                title='Interested in using MCP instead'
                onClick={() => onSelect('interested_but_not_allowed')}
                footer={<span className='paragraph-medium fw-bold zocks-text-gray-900'>I'll keep downloading</span>}
              >
                <span className='paragraph-medium zocks-text-gray-900'>It's just not available in my organization yet.</span>
                <LogoRow logos={MCP_LOGOS_MONO} />
              </Panel>
            )}
            <DownloadsWorkForMePanel onSelect={onSelect} />
          </div>
        </div>
      </div>
    }
  />
)
